// @ts-nocheck
/**
 * Every sheet a director keeps for one event, under one tab row.
 *
 * The tabs are all scoped to this event; "All events" sits apart at the right
 * and navigates away, because it is a different thing entirely. The active tab
 * lives in the URL so a bookmark or a pasted link lands where it was left.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import LiveSheet, { sheetWindowPath } from "@/components/events/LiveSheet";
import TabGrid from "@/components/events/sheet-tabs/TabGrid";
import ShareSheetDialog from "@/components/events/ShareSheetDialog";
import EventPacketDialog from "@/components/events/EventPacketDialog";
import { TAB_BY_KEY, TAB_DEFS, TabKey } from "@/components/events/sheet-tabs/tabDefs";
import { useEventRosters } from "@/hooks/useEventRosters";


const ALL_EVENTS_PATH = "/admin-dashboard/sheets/all-events";

export default function EventSheetTabs({
  eventId,
  eventName,
  /** Set when opened through a share link — the tab follows the link's scope. */
  shareToken,
  initialTab,
  popout = false,
}: {
  eventId: string;
  eventName?: string;
  shareToken?: string;
  initialTab?: TabKey;
  popout?: boolean;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab") as TabKey | null;
  const [tab, setTab] = useState<TabKey>(
    urlTab && TAB_BY_KEY[urlTab] ? urlTab : initialTab ?? "teams"
  );
  const [shareOpen, setShareOpen] = useState(false);
  const [packetOpen, setPacketOpen] = useState(false);

  const [counts, setCounts] = useState<Record<string, number>>({});
  const [needsGame, setNeedsGame] = useState(0);
  const [rosterRule, setRosterRule] = useState({ minimum: 6, deadline: null as string | null });


  const {
    rosters,
    loading: rostersLoading,
    error: rostersError,
    retry: retryRosters,
    submitFor,
  } = useEventRosters(shareToken ? null : eventId);

  useEffect(() => {
    const p = new URLSearchParams(searchParams);
    if (tab === "teams") p.delete("tab");
    else p.set("tab", tab);
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Counts on the tabs, so a director sees where the work is without clicking.
  useEffect(() => {
    if (!eventId || shareToken) return;
    let cancelled = false;
    (async () => {
      const tables = ["event_registrations_v2", "event_coaches", "media_credentials", "purchased_extras", "event_ticket_purchases"];
      const results = await Promise.all(
        tables.map((t) =>
          (supabase as any)
            .from(t)
            .select("id", { count: "exact", head: true })
            .eq("event_id", eventId)
        )
      );
      if (cancelled) return;
      const [regs, coaches, media, extras, tickets] = results.map((r: any) => r.count ?? 0);
      setCounts({
        teams: regs,
        roster: regs,
        checkin: regs,
        coaches,
        media,
        extras,
        tickets,
      });
      const { data: ev } = await (supabase as any)
        .from("events")
        .select("roster_minimum_players, roster_deadline")
        .eq("id", eventId)
        .maybeSingle();
      if (!cancelled && ev) {
        setRosterRule({
          minimum: ev.roster_minimum_players ?? 6,
          deadline: ev.roster_deadline ?? null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, shareToken]);

  const def = TAB_BY_KEY[tab];

  const openInNewWindow = () =>
    window.open(
      `${sheetWindowPath(eventId)}${tab === "teams" ? "" : `?tab=${tab}`}`,
      `sheet-${eventId}`,
      "noopener,width=1400,height=900"
    );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex items-center gap-1 border-b print:hidden">
        <div className="flex flex-1 flex-wrap items-center gap-1">
          {TAB_DEFS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`-mb-px inline-flex h-9 items-center gap-2 border-b-2 px-3 text-sm font-medium ${
                tab === t.key
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
              {counts[t.key] > 0 && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                  {counts[t.key]}
                </span>
              )}
              {t.key === "extras" && needsGame > 0 && (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-900">
                  {needsGame} need{needsGame === 1 ? "s" : ""} a game
                </span>
              )}
            </button>
          ))}

        </div>
        {!shareToken && (
          <button
            type="button"
            onClick={() => setPacketOpen(true)}
            className="ml-2 inline-flex h-8 items-center gap-1.5 rounded-md border border-input px-3 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            <FileText className="h-4 w-4" /> Rosters & players
          </button>
        )}
        {!shareToken && (
          <a
            href={ALL_EVENTS_PATH}
            className="ml-2 inline-flex h-8 items-center rounded-md border border-input px-3 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            All events →
          </a>
        )}
      </div>


      <div className="min-h-0 flex-1">
        {tab === "teams" ? (
          <LiveSheet eventId={eventId} popout={popout} shareToken={shareToken} />
        ) : (
          <TabGrid
            key={tab}
            tab={def}
            eventId={eventId}
            eventName={eventName}
            shareToken={shareToken}
            rlRosters={rosters}
            rosterRule={rosterRule}
            rostersLoading={rostersLoading}
            rostersError={rostersError}
            onRetryRosters={retryRosters}
            onSubmitRoster={(teamId, reason) => submitFor(teamId, reason)}
            onShare={() => setShareOpen(true)}
            onOpenWindow={popout ? undefined : openInNewWindow}
            onCount={(n) => setCounts((c) => ({ ...c, [tab]: n }))}
            onNeedsGame={setNeedsGame}

          />
        )}
      </div>

      <ShareSheetDialog
        eventId={eventId}
        scope={def.scope}
        tab={tab}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />

      <EventPacketDialog eventId={eventId} open={packetOpen} onOpenChange={setPacketOpen} />

    </div>
  );
}
